import { useFormikContext } from 'formik';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as styles from './TeammatesFilter.module.scss';
import TeammateTypeLabel from './TeammateTypeLabel';
import type {
  TeammatesFilterStatus,
  TeammatesFilterType,
} from './useTeammatesFilter';
import useTeammatesFilter from './useTeammatesFilter';
import Button from '../../components/Button';
import Form, { Field } from '../../components/Form';
import FormField from '../../components/formInternals/FormField';
import IconButton from '../../components/IconButton';
import Modal from '../../components/Modal';
import Tooltip from '../../components/Tooltip';
import { UserType } from '../../graphql/generated/graphql';
import closeIcon from '../../icons/non-prefilled/16x16/clear.svg';

const BILLABLE_TYPES: TeammatesFilterType[] = ['ADMIN', 'USER'];
const BILLABLE_STATUSES: TeammatesFilterStatus[] = ['ACTIVE'];

type FormValues = {
  types: TeammatesFilterType[];
  statuses: TeammatesFilterStatus[];
};

interface LabelWithTooltipProps {
  label: string;
  tooltip: string;
}
const LabelWithTooltip = ({ label, tooltip }: LabelWithTooltipProps) => (
  <Tooltip content={tooltip}>
    <span>{label}</span>
  </Tooltip>
);

interface FilterObserverProps {
  onChangeTypes: (types: TeammatesFilterType[]) => void;
  onChangeStatuses: (types: TeammatesFilterStatus[]) => void;
}

const FilterObserver = ({
  onChangeTypes,
  onChangeStatuses,
}: FilterObserverProps) => {
  const { values } = useFormikContext<FormValues>();

  // Formik doesn't have a great solution to listen to changes in the form values,
  // so we are stuck to use useEffect on the values from the context.
  // See https://github.com/jaredpalmer/formik/issues/271 for more information
  // In order to make sure that we eliminate the possibility of the cange handlers
  // causing a rerender, we use the latest ref pattern so that the change handlers
  // are decoupled from the useEffect
  const onChangeTypesRef = useRef(onChangeTypes);
  useLayoutEffect(() => {
    onChangeTypesRef.current = onChangeTypes;
  }, [onChangeTypes]);

  const onChangeStatusesRef = useRef(onChangeStatuses);
  useLayoutEffect(() => {
    onChangeStatusesRef.current = onChangeStatuses;
  }, [onChangeStatuses]);

  useEffect(() => {
    onChangeTypesRef.current(values.types);
  }, [values.types]);

  useEffect(() => {
    onChangeStatusesRef.current(values.statuses);
  }, [values.statuses]);

  return null;
};

interface OnlyBillableProps {
  types: TeammatesFilterType[];
  statuses: TeammatesFilterStatus[];
  onSelectOnlyBillable: () => void;
}
const OnlyBillable = ({
  types,
  statuses,
  onSelectOnlyBillable,
}: OnlyBillableProps) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [onlyBillable, setOnlyBillable] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const onlyBillableAreSelected =
    types.length === BILLABLE_TYPES.length &&
    statuses.length === BILLABLE_STATUSES.length &&
    BILLABLE_TYPES.every((billableType) => types.includes(billableType)) &&
    BILLABLE_STATUSES.every((billableStatus) =>
      statuses.includes(billableStatus),
    );

  useEffect(() => {
    if (!onlyBillableAreSelected) {
      setOnlyBillable(false);
    }
  }, [onlyBillableAreSelected]);

  return (
    <FormField
      label="Only billable users"
      type="checkbox"
      name="onlyBillable"
      checked={onlyBillable}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
        const { checked } = event.target;
        setOnlyBillable(checked);
        if (checked) {
          onSelectOnlyBillable();
        }
      }}
    />
  );
};

interface TeammatesFilterProps {
  asModal?: boolean;
  close?: () => void;
  filteredTeammates: number;
  requireInviteApproval: boolean;
}

const TeammatesFilter = ({
  asModal = false,
  close,
  filteredTeammates,
  requireInviteApproval,
}: TeammatesFilterProps) => {
  const { types, statuses, updateStatuses, updateTypes } = useTeammatesFilter();

  const inner = (
    <Form<FormValues> initialValues={{ types, statuses }} enableReinitialize>
      {({ values, setValues }) => (
        <>
          <FilterObserver
            onChangeTypes={updateTypes}
            onChangeStatuses={updateStatuses}
          />
          <div className={styles.filter}>
            <div className={styles.sections}>
              <div className={styles.section}>
                <div className={styles.sectionHeading}>Account type</div>
                <Field
                  label={<TeammateTypeLabel type={UserType.User} />}
                  type="checkbox"
                  name="types"
                  value="USER"
                />
                <Field
                  label={<TeammateTypeLabel type={UserType.Admin} />}
                  type="checkbox"
                  name="types"
                  value="ADMIN"
                />
                <Field
                  label={<TeammateTypeLabel type={UserType.Guest} />}
                  type="checkbox"
                  name="types"
                  value="GUEST"
                />
              </div>
              <div className={styles.section}>
                <div className={styles.sectionHeading}>Status</div>

                <Field
                  label={
                    <LabelWithTooltip
                      label="Active"
                      tooltip={`All users\nwho can log in`}
                    />
                  }
                  type="checkbox"
                  name="statuses"
                  value="ACTIVE"
                />
                <Field
                  label={
                    <LabelWithTooltip
                      label="Deactivated"
                      tooltip={`Former users who\ncan no longer log in`}
                    />
                  }
                  type="checkbox"
                  name="statuses"
                  value="DEACTIVATED"
                />
                <Field
                  label={
                    <LabelWithTooltip
                      label="Invited"
                      tooltip={`Invitations waiting\nfor users to accept`}
                    />
                  }
                  type="checkbox"
                  name="statuses"
                  value="INVITED"
                />
                {requireInviteApproval && (
                  <>
                    <Field
                      label={
                        <LabelWithTooltip
                          label="Pending"
                          tooltip={`Invitations waiting\nfor admin approval`}
                        />
                      }
                      type="checkbox"
                      name="statuses"
                      value="PENDING"
                    />
                    <Field
                      label={
                        <LabelWithTooltip
                          label="Rejected"
                          tooltip={`Invitation requests\nthat have been\nrejected by an admin`}
                        />
                      }
                      type="checkbox"
                      name="statuses"
                      value="REJECTED"
                    />
                  </>
                )}
              </div>
            </div>
            <div className={styles.bottomSection}>
              <OnlyBillable
                types={values.types}
                statuses={values.statuses}
                onSelectOnlyBillable={() => {
                  setValues({
                    types: BILLABLE_TYPES,
                    statuses: BILLABLE_STATUSES,
                  });
                }}
              />
            </div>
          </div>
        </>
      )}
    </Form>
  );

  return asModal ? (
    <Modal noPadding fullscreen>
      <div className={styles.modalContent}>
        <div>
          <div className={styles.modalTitle}>
            <h4>Filter</h4>
            <div>
              <IconButton
                onClick={close}
                applyNegativeMargin
                aria-label="Close"
              >
                {closeIcon}
              </IconButton>
            </div>
          </div>
          {inner}
        </div>
        <div className={styles.modalButton}>
          <Button variant="primary" block onClick={close}>
            Apply ({filteredTeammates} teammates)
          </Button>
        </div>
      </div>
    </Modal>
  ) : (
    inner
  );
};

export default TeammatesFilter;
